<template>
  <AdminShell
    title="数据统计"
    :tabs="tabs"
    :model-value="activeTab"
    padded
    @update:model-value="onTabChange"
  >
    <template #nav-right>
      <a-space>
        <a-range-picker
          :value="pickerValue"
          :allow-clear="false"
          format="YYYY-MM-DD"
          :disabled-date="disabledDate"
          @calendarChange="onCalendarChange"
          @openChange="onOpenChange"
          @change="onRangeChange"
        />
        <a-button type="primary" :loading="loading || detailLoading" @click="onQuery">查询</a-button>
      </a-space>
    </template>

    <a-table
      class="adm-table"
      :columns="columns"
      :data-source="rows"
      :loading="loading"
      :pagination="false"
      row-key="date"
      size="small"
    />

    <div class="adm-api-section stats-detail-section">
      <div class="stats-detail-header">
        <h3 class="adm-api-section-title" style="margin:0">数据详情</h3>
        <span class="adm-cell-muted">
          按入库时间（create_time）查询，共 {{ detailTotal.toLocaleString() }} 条
          <template v-if="detailDbTotal != null">
            （库内有效 {{ detailDbTotal.toLocaleString() }} 条）
          </template>
        </span>
      </div>
      <a-table
        class="adm-table"
        :columns="detailColumns"
        :data-source="detailRows"
        :loading="detailLoading"
        :pagination="false"
        :scroll="{ x: 'max-content' }"
        :row-key="detailRowKey"
        size="small"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'create_time' || column.key === 'update_time'">
            {{ formatCreateTime(record[column.key]) || '-' }}
          </template>
          <template v-else-if="column.key === 'resource_link' || column.key === 'source_link' || column.key === 'link'">
            <a
              v-if="record[column.key]"
              :href="record[column.key]"
              target="_blank"
              rel="noopener noreferrer"
              @click.stop
            >
              {{ record[column.key] }}
            </a>
            <span v-else>-</span>
          </template>
          <template v-else>
            {{ formatCellText(record[column.dataIndex]) || '-' }}
          </template>
        </template>
      </a-table>
      <div class="table-pagination">
        <div class="pagination-left">
          <div class="page-size-selector">
            <span class="page-size-text">每页</span>
            <a-select
              :value="detailLimit"
              size="small"
              class="page-size-select"
              @change="onDetailPageSizeChange"
            >
              <a-select-option :value="10">10</a-select-option>
              <a-select-option :value="20">20</a-select-option>
              <a-select-option :value="50">50</a-select-option>
              <a-select-option :value="100">100</a-select-option>
            </a-select>
            <span class="page-size-text">条</span>
          </div>
          <span class="total-text">共 {{ detailTotal.toLocaleString() }} 条</span>
        </div>
        <a-pagination
          :current="detailPage"
          :total="detailTotal"
          :page-size="detailLimit"
          :show-size-changer="false"
          :show-quick-jumper="false"
          class="pagination-component"
          @change="onDetailPageChange"
        />
      </div>
    </div>
  </AdminShell>
</template>

<script>
import dayjs from 'dayjs'
import { message } from 'ant-design-vue'
import AdminShell from '@/components/admin/AdminShell.vue'
import { getDailyStatistics } from '@/api/adminApi'
import { getDataByDate } from '@/api/filterApi'
import { getColumnsByLabelGroup } from '@/utils/columnConfig'
import { formatCreateTime } from '@/utils/formatCreateTime'

/** 与前台侧边栏展示顺序一致；data_type 对应 labelGroup */
const TABS = [
  { key: '5', label: '器械注册查询' },
  { key: '6', label: '临床研究' },
  { key: '4', label: '前沿技术' },
  { key: '2', label: '国家政策' },
  { key: '1', label: '企业/市场端' },
  { key: '3', label: '招投标信息' },
]

const MAX_RANGE_DAYS = 31

function formatYmd(d) {
  return d.format('YYYY-MM-DD')
}

export default {
  name: 'StatisticsView',
  components: { AdminShell },
  data() {
    return {
      tabs: TABS,
      activeTab: TABS[0].key,
      dateRange: [dayjs().subtract(6, 'day'), dayjs()],
      /** 面板选择过程中的临时值，用于限制跨度 */
      selectingDates: null,
      loading: false,
      rows: [],
      columns: [
        { title: '日期', dataIndex: 'date', key: 'date', width: 140 },
        { title: '数据总量', dataIndex: 'data_count', key: 'data_count' },
        { title: '清洗成功', dataIndex: 'clean_success_count', key: 'clean_success_count' },
        { title: '清洗失败', dataIndex: 'clean_failed_count', key: 'clean_failed_count' },
        { title: '索引成功', dataIndex: 'index_success_count', key: 'index_success_count' },
        { title: '索引失败', dataIndex: 'index_failed_count', key: 'index_failed_count' },
      ],
      detailLoading: false,
      detailRows: [],
      detailTotal: 0,
      detailDbTotal: null,
      detailPage: 1,
      detailLimit: 10,
    }
  },
  computed: {
    pickerValue() {
      return this.selectingDates || this.dateRange
    },
    detailColumns() {
      return getColumnsByLabelGroup(Number(this.activeTab))
        .filter((col) => col.visible !== false)
        .map((col) => ({
          title: col.label,
          dataIndex: col.key,
          key: col.key,
          ellipsis: true,
          width: col.maxWidth || undefined,
        }))
    },
  },
  mounted() {
    this.onQuery()
  },
  methods: {
    disabledDate(current) {
      if (!current) return false
      if (current.isAfter(dayjs().endOf('day'))) return true

      const dates = this.selectingDates
      if (!dates || (!dates[0] && !dates[1])) return false

      // 最多 31 个自然日：起止日期间隔 ≤ 30 天
      const tooLate = dates[0] && current.diff(dates[0], 'day') >= MAX_RANGE_DAYS
      const tooEarly = dates[1] && dates[1].diff(current, 'day') >= MAX_RANGE_DAYS
      return !!(tooEarly || tooLate)
    },
    onCalendarChange(dates) {
      this.selectingDates = dates
    },
    onOpenChange(open) {
      if (open) {
        this.selectingDates = [null, null]
      } else {
        this.selectingDates = null
      }
    },
    onRangeChange(dates) {
      this.dateRange = dates
      this.selectingDates = null
    },
    onTabChange(key) {
      this.activeTab = key
      this.detailPage = 1
      this.onQuery()
    },
    onQuery() {
      this.detailPage = 1
      this.loadData()
      this.loadDetail()
    },
    validateRange() {
      const [start, end] = this.dateRange || []
      if (!start || !end) {
        message.warning('请选择开始和结束日期')
        return null
      }
      if (start.isAfter(end, 'day')) {
        message.warning('开始时间不能晚于结束时间')
        return null
      }
      const days = end.startOf('day').diff(start.startOf('day'), 'day') + 1
      if (days > MAX_RANGE_DAYS) {
        message.warning('时间范围不能超过一个月（31天）')
        return null
      }
      return {
        start_date: formatYmd(start),
        end_date: formatYmd(end),
      }
    },
    async loadData() {
      const range = this.validateRange()
      if (!range) return

      this.loading = true
      try {
        const res = await getDailyStatistics({
          ...range,
          data_type: String(this.activeTab),
        })
        this.rows = Array.isArray(res.data) ? res.data : []
      } catch {
        this.rows = []
      } finally {
        this.loading = false
      }
    },
    async loadDetail() {
      const range = this.validateRange()
      if (!range) return

      this.detailLoading = true
      try {
        const res = await getDataByDate(this.activeTab, {
          ...range,
          page: this.detailPage,
          limit: this.detailLimit,
        })
        const data = res?.data || {}
        this.detailRows = Array.isArray(data.list) ? data.list : []
        this.detailTotal = Number(data.total) || 0
        this.detailDbTotal = data.db_total_rows != null ? Number(data.db_total_rows) : null
      } catch {
        this.detailRows = []
        this.detailTotal = 0
        this.detailDbTotal = null
      } finally {
        this.detailLoading = false
      }
    },
    onDetailPageChange(page) {
      this.detailPage = page
      this.loadDetail()
    },
    onDetailPageSizeChange(limit) {
      this.detailLimit = limit
      this.detailPage = 1
      this.loadDetail()
    },
    detailRowKey(record, index) {
      return record.id ?? record._id ?? `${this.detailPage}-${index}`
    },
    formatCellText(value) {
      if (value === null || value === undefined || value === '') return ''
      if (Array.isArray(value)) {
        return value.map((item) => String(item).trim()).filter(Boolean).join('、')
      }
      return String(value)
    },
    formatCreateTime,
  },
}
</script>

<style scoped>
.stats-detail-section {
  margin-top: 24px;
}

.stats-detail-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.table-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 12px;
  flex-wrap: wrap;
}

.pagination-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.page-size-selector {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-size-text,
.total-text {
  color: rgba(0, 0, 0, 0.45);
  font-size: 13px;
}

.page-size-select {
  width: 72px;
}
</style>
